import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  procedureTemplateService,
  type CreateProcedureDto,
  type UpdateProcedureDto,
  type CloneProcedureDto,
  type QuickEditProcedureDto,
} from '@/services/procedure-template.service';
import { procedureKeys } from './useProcedureTemplates';

export function useCreateProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateProcedureDto) => procedureTemplateService.create(dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
    },
  });
}

export function useUpdateProcedure(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateProcedureDto) => procedureTemplateService.update(id, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.detail(id) });
    },
  });
}

export function useDeleteProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => procedureTemplateService.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
    },
  });
}

export function usePublishProcedure(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => procedureTemplateService.publish(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
      void qc.invalidateQueries({ queryKey: procedureKeys.versions(id) });
    },
  });
}

export function useArchiveProcedure(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => procedureTemplateService.archive(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
      void qc.invalidateQueries({ queryKey: procedureKeys.versions(id) });
    },
  });
}

export function useNewVersionProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => procedureTemplateService.newVersion(sourceId),
    onSuccess: (_, sourceId) => {
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
      void qc.invalidateQueries({ queryKey: procedureKeys.versions(sourceId) });
    },
  });
}

export function useCloneProcedure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CloneProcedureDto }) =>
      procedureTemplateService.clone(id, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
    },
  });
}

export function useQuickEditProcedure(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: QuickEditProcedureDto) => procedureTemplateService.quickEdit(id, dto),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: procedureKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: procedureKeys.lists() });
    },
  });
}
